<template>
  <Menu class="w-[280px]">
    <template v-if="isOwner && originFlagEnabled && billingStore.isPro">
      <Menu.Item
        :icon="GlobeAddIcon"
        :disabled="isFileInactive"
        @click="handleAction(handleRegisterCountriesClick)"
      >
        Register in more countries
        <template #description>
          Recognized and registered under local laws
        </template>
      </Menu.Item>

      <Menu.Divider />
    </template>

    <Menu.Item
      :icon="ShareIcon"
      :disabled="isFileInactive"
      @click="handleAction(handleShareClick)"
    >
      Share
    </Menu.Item>
    <Menu.Item
      :icon="SendIcon"
      :disabled="isFileInactive"
      @click="handleAction(handleInviteClick)"
    >
      Invite
    </Menu.Item>
    <Menu.Item
      :icon="DownloadIcon"
      :disabled="!canDownload"
      @click="handleAction(handleDownloadClick)"
    >
      Download
    </Menu.Item>

    <Menu.Divider />

    <Menu.Item :icon="GearIcon" @click="handleAction(handleFileSettingsClick)">
      Copyright settings
    </Menu.Item>

    <Menu.Item
      :icon="LockIcon"
      :disabled="isFileInactive"
      @click="handleAction(handleFilePrivacySettingsClick)"
    >
      Privacy controls
    </Menu.Item>

    <Menu.Item :icon="MoveIcon" @click="handleAction(handleMoveClick)">
      Move
    </Menu.Item>

    <Menu.Item
      class="text-red-600!"
      :icon="ArchivedIcon"
      @click="handleAction(handleArchiveClick)"
    >
      Archive
    </Menu.Item>

    <Menu.Divider />

    <Menu.Item
      :icon="DacrMarkIcon"
      :disabled="isFileInactive || !certificateUrl"
      @click="handleViewCertificateClick"
    >
      View Dacr Certificate
    </Menu.Item>
  </Menu>
</template>

<script setup lang="ts">
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { useBillingStore } from '@/features/billing/store'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import {
  CopyrightStatus,
  FilePermission,
  FileRole,
  type File,
} from '@/shared/graphql/router/graphql'
import { computed } from 'vue'
import { Menu } from '@dacr/library'
import {
  ShareIcon,
  SendIcon,
  DownloadIcon,
  GearIcon,
  LockIcon,
  MoveIcon,
  ArchivedIcon,
  DacrMarkIcon,
  GlobeAddIcon,
} from '@dacr/icons'

const { file } = defineProps<{
  file: File
}>()
const emit = defineEmits<{
  clicked: []
}>()

const isFileInactive = computed(
  () => file.copyright?.status === CopyrightStatus.Inactive
)

const isOwner = computed(() => file.role === FileRole.Owner)

const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const billingStore = useBillingStore()

const canDownload = computed(() =>
  file.permissions.includes(FilePermission.Download)
)

const certificateUrl = computed(
  () => file.copyright?.registrations?.[0]?.certificateUrl
)

const handleAction = (fn: () => void) => {
  fn()
  emit('clicked')
}

const handleShareClick = () => {
  openDialog(Dialog.Share, { props: { file } })
}

const handleInviteClick = () => {
  openDialog(Dialog.FileAccess, { props: { file } })
}

const handleDownloadClick = () => {
  openModal(Modals.Download, { props: { file } })
}

const handleFileSettingsClick = () => {
  openDialog(Dialog.FileSettings, { props: { file } })
}

const handleRegisterCountriesClick = () => {
  openDialog(Dialog.RegisterCountries, { props: { files: [file] } })
}

const handleFilePrivacySettingsClick = () => {
  openDialog(Dialog.FilePrivacySettings, { props: { file } })
}

const handleArchiveClick = () => {
  openModal(Modals.ArchiveFiles, { props: { files: [file] } })
}

const handleMoveClick = () => {
  openDialog(Dialog.MoveItems, { props: { ids: [file.id] } })
}

const handleViewCertificateClick = () => {
  if (!certificateUrl.value) return
  window.open(certificateUrl.value, '_blank')
  emit('clicked')
}
</script>
